import { useContext, useEffect, useRef } from 'react'
import { ReactReduxContext } from 'react-redux'
import { fetchNewsletterDefault } from '../utils/newsletterDefault'

// SPEC-109 FR-4 (render flow): a signed-out visitor's newsletter box starts
// unticked (first paint at its final size, usable at once) and is ticked
// when the backend's per-country default says so — only if the person
// hasn't touched it meanwhile; it is never unticked. A signed-in member
// (gift redemption) keeps an unticked box and no request is made. The
// request is the page's one shared promise: App starts it when a route that
// can show the box loads, and this starts it if nothing did.
//
//   const { markTouched, touched } = useNewsletterDefault({ setValue, getValues })
//   <NewsletterConsent inputProps={register('marketingConsent')} onTouched={markTouched} />
//   … on submit: marketingConsentTouched: touched.current

// Signed-in or not, read once at mount; the forms also render without a
// store (tests, the plain register page), which counts as signed out.
const useSignedInAtMount = () => {
  const redux = useContext(ReactReduxContext)
  const signedIn = useRef(null)
  if (signedIn.current === null) {
    const state = redux && redux.store ? redux.store.getState() : null
    signedIn.current = Boolean(state && state.auth && state.auth.token)
  }
  return signedIn.current
}

export default function useNewsletterDefault(
  { setValue, getValues },
  name = 'marketingConsent'
) {
  const touched = useRef(false)
  const signedIn = useSignedInAtMount()
  useEffect(() => {
    if (signedIn) return undefined
    let active = true
    fetchNewsletterDefault().then(({ defaultTicked }) => {
      if (!active || !defaultTicked || touched.current) return
      if (!getValues(name)) setValue(name, true, { shouldDirty: false })
    })
    return () => {
      active = false
    }
  }, [])
  return {
    touched,
    markTouched: () => {
      touched.current = true
    }
  }
}
